import { format } from 'date-fns'

/**
 * Comprehensive timezone utility library for Rainfactory
 * Provides timezone conversion, formatting, and management utilities
 */

// IANA timezone identifiers organized by region
export const TIMEZONE_REGIONS = {
  AMERICAS: {
    label: 'Americas',
    timezones: [
      { value: 'America/New_York', label: 'Eastern Time (US & Canada)', region: 'North America' },
      { value: 'America/Chicago', label: 'Central Time (US & Canada)', region: 'North America' },
      { value: 'America/Denver', label: 'Mountain Time (US & Canada)', region: 'North America' },
      { value: 'America/Los_Angeles', label: 'Pacific Time (US & Canada)', region: 'North America' },
      { value: 'America/Anchorage', label: 'Alaska Time', region: 'North America' },
      { value: 'Pacific/Honolulu', label: 'Hawaii Time', region: 'North America' },
      { value: 'America/Toronto', label: 'Eastern Time (Canada)', region: 'North America' },
      { value: 'America/Vancouver', label: 'Pacific Time (Canada)', region: 'North America' },
      { value: 'America/Mexico_City', label: 'Mexico City', region: 'North America' },
      { value: 'America/Sao_Paulo', label: 'São Paulo', region: 'South America' },
      { value: 'America/Buenos_Aires', label: 'Buenos Aires', region: 'South America' },
      { value: 'America/Lima', label: 'Lima', region: 'South America' },
      { value: 'America/Bogota', label: 'Bogotá', region: 'South America' },
      { value: 'America/Santiago', label: 'Santiago', region: 'South America' },
    ]
  },
  EUROPE: {
    label: 'Europe & Africa',
    timezones: [
      { value: 'Europe/London', label: 'London (GMT/BST)', region: 'Western Europe' },
      { value: 'Europe/Dublin', label: 'Dublin', region: 'Western Europe' },
      { value: 'Europe/Paris', label: 'Paris (CET/CEST)', region: 'Western Europe' },
      { value: 'Europe/Berlin', label: 'Berlin', region: 'Western Europe' },
      { value: 'Europe/Rome', label: 'Rome', region: 'Western Europe' },
      { value: 'Europe/Madrid', label: 'Madrid', region: 'Western Europe' },
      { value: 'Europe/Amsterdam', label: 'Amsterdam', region: 'Western Europe' },
      { value: 'Europe/Brussels', label: 'Brussels', region: 'Western Europe' },
      { value: 'Europe/Zurich', label: 'Zurich', region: 'Western Europe' },
      { value: 'Europe/Vienna', label: 'Vienna', region: 'Central Europe' },
      { value: 'Europe/Prague', label: 'Prague', region: 'Central Europe' },
      { value: 'Europe/Budapest', label: 'Budapest', region: 'Central Europe' },
      { value: 'Europe/Warsaw', label: 'Warsaw', region: 'Central Europe' },
      { value: 'Europe/Stockholm', label: 'Stockholm', region: 'Northern Europe' },
      { value: 'Europe/Helsinki', label: 'Helsinki', region: 'Northern Europe' },
      { value: 'Europe/Oslo', label: 'Oslo', region: 'Northern Europe' },
      { value: 'Europe/Copenhagen', label: 'Copenhagen', region: 'Northern Europe' },
      { value: 'Europe/Moscow', label: 'Moscow', region: 'Eastern Europe' },
      { value: 'Europe/Kiev', label: 'Kyiv', region: 'Eastern Europe' },
      { value: 'Europe/Istanbul', label: 'Istanbul', region: 'Eastern Europe' },
      { value: 'Africa/Cairo', label: 'Cairo', region: 'Africa' },
      { value: 'Africa/Johannesburg', label: 'Johannesburg', region: 'Africa' },
      { value: 'Africa/Lagos', label: 'Lagos', region: 'Africa' },
      { value: 'Africa/Casablanca', label: 'Casablanca', region: 'Africa' },
    ]
  },
  ASIA_PACIFIC: {
    label: 'Asia & Pacific',
    timezones: [
      { value: 'Asia/Tokyo', label: 'Tokyo (JST)', region: 'East Asia' },
      { value: 'Asia/Seoul', label: 'Seoul', region: 'East Asia' },
      { value: 'Asia/Shanghai', label: 'Shanghai (CST)', region: 'East Asia' },
      { value: 'Asia/Hong_Kong', label: 'Hong Kong', region: 'East Asia' },
      { value: 'Asia/Taipei', label: 'Taipei', region: 'East Asia' },
      { value: 'Asia/Singapore', label: 'Singapore', region: 'Southeast Asia' },
      { value: 'Asia/Bangkok', label: 'Bangkok', region: 'Southeast Asia' },
      { value: 'Asia/Manila', label: 'Manila', region: 'Southeast Asia' },
      { value: 'Asia/Jakarta', label: 'Jakarta', region: 'Southeast Asia' },
      { value: 'Asia/Kuala_Lumpur', label: 'Kuala Lumpur', region: 'Southeast Asia' },
      { value: 'Asia/Ho_Chi_Minh', label: 'Ho Chi Minh City', region: 'Southeast Asia' },
      { value: 'Asia/Kolkata', label: 'Mumbai/Delhi (IST)', region: 'South Asia' },
      { value: 'Asia/Karachi', label: 'Karachi', region: 'South Asia' },
      { value: 'Asia/Dhaka', label: 'Dhaka', region: 'South Asia' },
      { value: 'Asia/Dubai', label: 'Dubai (GST)', region: 'Middle East' },
      { value: 'Asia/Riyadh', label: 'Riyadh', region: 'Middle East' },
      { value: 'Asia/Tehran', label: 'Tehran', region: 'Middle East' },
      { value: 'Australia/Sydney', label: 'Sydney (AEDT/AEST)', region: 'Australia' },
      { value: 'Australia/Melbourne', label: 'Melbourne', region: 'Australia' },
      { value: 'Australia/Brisbane', label: 'Brisbane', region: 'Australia' },
      { value: 'Australia/Perth', label: 'Perth', region: 'Australia' },
      { value: 'Pacific/Auckland', label: 'Auckland', region: 'New Zealand' },
      { value: 'Pacific/Fiji', label: 'Fiji', region: 'Pacific' },
    ]
  },
  COMMON: {
    label: 'Common Timezones',
    timezones: [
      { value: 'UTC', label: 'UTC (Coordinated Universal Time)', region: 'UTC' },
      { value: 'America/New_York', label: 'Eastern Time (US)', region: 'North America' },
      { value: 'America/Los_Angeles', label: 'Pacific Time (US)', region: 'North America' },
      { value: 'Europe/London', label: 'London (GMT/BST)', region: 'Europe' },
      { value: 'Europe/Paris', label: 'Paris (CET/CEST)', region: 'Europe' },
      { value: 'Asia/Tokyo', label: 'Tokyo (JST)', region: 'Asia' },
      { value: 'Asia/Shanghai', label: 'Shanghai (CST)', region: 'Asia' },
      { value: 'Australia/Sydney', label: 'Sydney (AEDT/AEST)', region: 'Australia' },
    ]
  }
} as const

// Flatten all timezones for easy access
export const ALL_TIMEZONES = [
  ...TIMEZONE_REGIONS.COMMON.timezones,
  ...TIMEZONE_REGIONS.AMERICAS.timezones,
  ...TIMEZONE_REGIONS.EUROPE.timezones,
  ...TIMEZONE_REGIONS.ASIA_PACIFIC.timezones,
].reduce((acc, tz) => {
  if (!acc.find(existing => existing.value === tz.value)) {
    acc.push(tz)
  }
  return acc
}, [] as Array<{ value: string; label: string; region: string }>)

// TypeScript interfaces
export interface TimezoneInfo {
  value: string
  label: string
  region: string
  offset?: string
  abbreviation?: string
}

export interface FormattedDate {
  date: Date
  formatted: string
  timezone: string
  offset: string
  abbreviation: string
}

export interface TimezoneOffset {
  offset: number // in minutes
  offsetString: string // e.g., "+05:30"
  abbreviation: string // e.g., "EST", "PST"
  isDST: boolean
}

/**
 * Auto-detects the user's timezone using the browser's built-in API
 */
export function detectUserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch (error) {
    console.warn('Failed to detect user timezone:', error)
    return 'UTC'
  }
}

/**
 * Validates if a timezone string is a valid IANA timezone identifier
 */
export function isValidTimezone(timezone: string): boolean {
  try {
    new Intl.DateTimeFormat('en', { timeZone: timezone })
    return true
  } catch (error) {
    return false
  }
}

/**
 * Gets detailed timezone information including offset, abbreviation, and DST status
 */
export function getTimezoneInfo(timezone: string, date: Date = new Date()): TimezoneOffset {
  try {
    // Get timezone offset in minutes (note: getTimezoneOffset returns opposite sign)
    const tempDate = new Date(date.toLocaleString('en-US', { timeZone: timezone }))
    const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }))
    const offset = (utcDate.getTime() - tempDate.getTime()) / (1000 * 60)

    // Format offset string
    const hours = Math.floor(Math.abs(offset) / 60)
    const minutes = Math.abs(offset) % 60
    const sign = offset <= 0 ? '+' : '-'
    const offsetString = `${sign}${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`

    // Get timezone abbreviation using Intl.DateTimeFormat
    const formatter = new Intl.DateTimeFormat('en', {
      timeZone: timezone,
      timeZoneName: 'short'
    })
    const parts = formatter.formatToParts(date)
    const abbreviation = parts.find(part => part.type === 'timeZoneName')?.value || 'Unknown'

    // Determine if DST is active (simplified check)
    // We'll handle this in the separate isDSTActive function
    const isDST = false // Placeholder, will be properly calculated in isDSTActive function

    return {
      offset,
      offsetString,
      abbreviation,
      isDST
    }
  } catch (error) {
    console.error('Error getting timezone info:', error)
    return {
      offset: 0,
      offsetString: '+00:00',
      abbreviation: 'UTC',
      isDST: false
    }
  }
}

/**
 * Formats a date in the user's specified timezone
 */
export function formatDateInTimezone(
  date: Date | string,
  timezone: string,
  formatString: string = 'MMM dd, yyyy h:mm a'
): string {
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date
    
    // Use native Intl.DateTimeFormat for better timezone handling
    if (formatString === 'full') {
      return dateObj.toLocaleString('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short'
      })
    }

    // Use a simplified approach since date-fns-tz is not available
    const zonedDate = new Date(dateObj.toLocaleString('en-US', { timeZone: timezone }))
    return format(zonedDate, formatString)
  } catch (error) {
    console.error('Error formatting date in timezone:', error)
    return format(typeof date === 'string' ? new Date(date) : date, formatString)
  }
}

/**
 * Gets the current time in a specific timezone
 */
export function getCurrentTimeInTimezone(timezone: string): Date {
  try {
    const now = new Date()
    return new Date(now.toLocaleString('en-US', { timeZone: timezone }))
  } catch (error) {
    console.error('Error getting current time in timezone:', error)
    return new Date()
  }
}

/**
 * Converts a UTC timestamp to a specific timezone
 */
export function convertUTCToTimezone(utcDate: Date | string, timezone: string): Date {
  try {
    const dateObj = typeof utcDate === 'string' ? new Date(utcDate) : utcDate
    return new Date(dateObj.toLocaleString('en-US', { timeZone: timezone }))
  } catch (error) {
    console.error('Error converting UTC to timezone:', error)
    return typeof utcDate === 'string' ? new Date(utcDate) : utcDate
  }
}

/**
 * Converts a date from one timezone to another
 */
export function convertTimezone(date: Date | string, fromTimezone: string, toTimezone: string): Date {
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date
    
    // First, treat the date as if it's in the source timezone and get UTC
    const utcTime = new Date(dateObj.toLocaleString('en-US', { timeZone: 'UTC' }))
    const fromTime = new Date(dateObj.toLocaleString('en-US', { timeZone: fromTimezone }))
    const offsetDiff = utcTime.getTime() - fromTime.getTime()
    const adjustedUTC = new Date(dateObj.getTime() + offsetDiff)
    
    // Then convert to target timezone
    return new Date(adjustedUTC.toLocaleString('en-US', { timeZone: toTimezone }))
  } catch (error) {
    console.error('Error converting between timezones:', error)
    return typeof date === 'string' ? new Date(date) : date
  }
}

/**
 * Gets a formatted date with comprehensive timezone information
 */
export function getFormattedDateWithTimezone(
  date: Date | string,
  timezone: string,
  formatString: string = 'MMM dd, yyyy h:mm a'
): FormattedDate {
  const dateObj = typeof date === 'string' ? new Date(date) : date
  const timezoneInfo = getTimezoneInfo(timezone, dateObj)
  
  return {
    date: dateObj,
    formatted: formatDateInTimezone(dateObj, timezone, formatString),
    timezone,
    offset: timezoneInfo.offsetString,
    abbreviation: timezoneInfo.abbreviation
  }
}

/**
 * Gets timezone display name (human-readable)
 */
export function getTimezoneDisplayName(timezone: string): string {
  const timezoneObj = ALL_TIMEZONES.find(tz => tz.value === timezone)
  return timezoneObj?.label || timezone
}

/**
 * Gets timezone abbreviation for a specific date (handles DST changes)
 */
export function getTimezoneAbbreviation(timezone: string, date: Date = new Date()): string {
  try {
    const formatter = new Intl.DateTimeFormat('en', {
      timeZone: timezone,
      timeZoneName: 'short'
    })
    const parts = formatter.formatToParts(date)
    return parts.find(part => part.type === 'timeZoneName')?.value || 'Unknown'
  } catch (error) {
    console.error('Error getting timezone abbreviation:', error)
    return 'Unknown'
  }
}

/**
 * Checks if a timezone observes DST at a given date
 */
export function isDSTActive(timezone: string, date: Date = new Date()): boolean {
  try {
    const januaryOffset = getTimezoneInfo(timezone, new Date(date.getFullYear(), 0, 1)).offset
    const julyOffset = getTimezoneInfo(timezone, new Date(date.getFullYear(), 6, 1)).offset
    const currentOffset = getTimezoneInfo(timezone, date).offset
    
    // If January and July offsets are different, DST is observed
    // DST is active if current offset matches the "more positive" offset (summer time)
    return januaryOffset !== julyOffset && currentOffset === Math.max(januaryOffset, julyOffset)
  } catch (error) {
    console.error('Error checking DST status:', error)
    return false
  }
}

/**
 * Gets a list of timezones for a dropdown/select component
 */
export function getTimezoneOptions(includeOffset: boolean = true): Array<{ value: string; label: string; group?: string }> {
  const options: Array<{ value: string; label: string; group?: string }> = []

  Object.entries(TIMEZONE_REGIONS).forEach(([, region]) => {
    region.timezones.forEach(timezone => {
      let label: string = timezone.label
      if (includeOffset && timezone.value !== 'UTC') {
        const info = getTimezoneInfo(timezone.value)
        label = `${timezone.label} (${info.offsetString})`
      }
      
      options.push({
        value: timezone.value,
        label,
        group: region.label
      })
    })
  })

  return options
}

/**
 * Formats time only (no date) in timezone
 */
export function formatTimeInTimezone(
  date: Date | string,
  timezone: string,
  use24Hour: boolean = false
): string {
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date
    return dateObj.toLocaleTimeString('en-US', {
      timeZone: timezone,
      hour12: !use24Hour,
      hour: 'numeric',
      minute: '2-digit'
    })
  } catch (error) {
    console.error('Error formatting time in timezone:', error)
    const dateObj = typeof date === 'string' ? new Date(date) : date
    return dateObj.toLocaleTimeString('en-US', {
      hour12: !use24Hour,
      hour: 'numeric',
      minute: '2-digit'
    })
  }
}

/**
 * Gets the timezone offset in hours (for calculations)
 */
export function getTimezoneOffsetHours(timezone: string, date: Date = new Date()): number {
  const info = getTimezoneInfo(timezone, date)
  return info.offset / 60
}

/**
 * Relative time configuration and types
 */
export interface RelativeTimeOptions {
  /** Whether to show seconds for very recent times */
  includeSeconds?: boolean
  /** Minimum unit to display (seconds, minutes, hours, days, weeks, months) */
  minUnit?: 'seconds' | 'minutes' | 'hours' | 'days' | 'weeks' | 'months'
  /** Maximum unit before switching to absolute date */
  maxUnit?: 'minutes' | 'hours' | 'days' | 'weeks' | 'months' | 'never'
  /** Threshold in days after which to show absolute date (overrides maxUnit) */
  absoluteThreshold?: number
  /** Whether to show 'ago' suffix for past times */
  showSuffix?: boolean
  /** Whether to use short format ('5m ago' vs '5 minutes ago') */
  shortFormat?: boolean
  /** Custom timezone to calculate relative to (defaults to user's timezone) */
  relativeTo?: string
}

export interface RelativeTimeResult {
  /** The relative time string ('2 hours ago', 'in 5 minutes') */
  relative: string
  /** Whether this is a past or future time */
  isPast: boolean
  /** Whether to show absolute time instead of relative */
  shouldShowAbsolute: boolean
  /** Absolute formatted date for fallback */
  absolute: string
  /** Time difference in milliseconds */
  diffMs: number
  /** The time unit used ('seconds', 'minutes', 'hours', etc.) */
  unit: string
  /** Numeric value for the relative time */
  value: number
}

/**
 * Gets relative time string with timezone awareness
 * Examples: "2 hours ago", "in 3 days", "Tomorrow at 2 PM", "Yesterday"
 */
export function getRelativeTime(
  date: Date | string,
  timezone: string = DEFAULT_TIMEZONE,
  options: RelativeTimeOptions = {}
): RelativeTimeResult {
  const {
    includeSeconds = false,
    minUnit = includeSeconds ? 'seconds' : 'minutes',
    maxUnit = 'never',
    absoluteThreshold = 7, // 7 days
    showSuffix = true,
    shortFormat = false,
    relativeTo = timezone
  } = options

  const dateObj = typeof date === 'string' ? new Date(date) : date
  const now = getCurrentTimeInTimezone(relativeTo)
  const diffMs = dateObj.getTime() - now.getTime()
  const isPast = diffMs < 0
  const absDiffMs = Math.abs(diffMs)

  // Calculate time units
  const seconds = Math.floor(absDiffMs / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)
  const weeks = Math.floor(days / 7)
  const months = Math.floor(days / 30)
  const years = Math.floor(days / 365)

  // Determine if we should show absolute time
  const shouldShowAbsolute = (
    (absoluteThreshold > 0 && days >= absoluteThreshold) ||
    (maxUnit === 'minutes' && hours >= 1) ||
    (maxUnit === 'hours' && days >= 1) ||
    (maxUnit === 'days' && weeks >= 1) ||
    (maxUnit === 'weeks' && months >= 1) ||
    (maxUnit === 'months' && years >= 1)
  )

  // Generate absolute fallback
  const absolute = formatDateInTimezone(dateObj, timezone, 'MMM dd, yyyy h:mm a')

  if (shouldShowAbsolute) {
    return {
      relative: absolute,
      isPast,
      shouldShowAbsolute: true,
      absolute,
      diffMs,
      unit: 'absolute',
      value: 0
    }
  }

  // Special cases for days with times
  const userTimezoneDate = formatDateInTimezone(dateObj, timezone, 'yyyy-MM-dd')
  const todayInUserTimezone = formatDateInTimezone(now, timezone, 'yyyy-MM-dd')
  const tomorrowDate = new Date(now)
  tomorrowDate.setDate(tomorrowDate.getDate() + 1)
  const tomorrowInUserTimezone = formatDateInTimezone(tomorrowDate, timezone, 'yyyy-MM-dd')
  const yesterdayDate = new Date(now)
  yesterdayDate.setDate(yesterdayDate.getDate() - 1)
  const yesterdayInUserTimezone = formatDateInTimezone(yesterdayDate, timezone, 'yyyy-MM-dd')

  // Check for today/tomorrow/yesterday
  if (userTimezoneDate === todayInUserTimezone && hours < 24) {
    const timeStr = formatTimeInTimezone(dateObj, timezone, false)
    if (hours < 6) { // Same day, close time
      // Fall through to hours/minutes
    } else {
      return {
        relative: `Today at ${timeStr}`,
        isPast,
        shouldShowAbsolute: false,
        absolute,
        diffMs,
        unit: 'today',
        value: 0
      }
    }
  } else if (userTimezoneDate === tomorrowInUserTimezone) {
    const timeStr = formatTimeInTimezone(dateObj, timezone, false)
    return {
      relative: `Tomorrow at ${timeStr}`,
      isPast,
      shouldShowAbsolute: false,
      absolute,
      diffMs,
      unit: 'tomorrow',
      value: 1
    }
  } else if (userTimezoneDate === yesterdayInUserTimezone) {
    const timeStr = formatTimeInTimezone(dateObj, timezone, false)
    return {
      relative: `Yesterday at ${timeStr}`,
      isPast,
      shouldShowAbsolute: false,
      absolute,
      diffMs,
      unit: 'yesterday',
      value: -1
    }
  }

  // Generate relative time string
  let relative: string
  let unit: string
  let value: number

  if (years >= 1) {
    unit = 'years'
    value = years
    const yearText = shortFormat ? 'y' : (years === 1 ? 'year' : 'years')
    relative = `${years} ${yearText}`
  } else if (months >= 1) {
    unit = 'months'  
    value = months
    const monthText = shortFormat ? 'mo' : (months === 1 ? 'month' : 'months')
    relative = `${months} ${monthText}`
  } else if (weeks >= 1) {
    unit = 'weeks'
    value = weeks
    const weekText = shortFormat ? 'w' : (weeks === 1 ? 'week' : 'weeks')
    relative = `${weeks} ${weekText}`
  } else if (days >= 1) {
    unit = 'days'
    value = days
    const dayText = shortFormat ? 'd' : (days === 1 ? 'day' : 'days')
    relative = `${days} ${dayText}`
  } else if (hours >= 1 || minUnit === 'hours') {
    unit = 'hours'
    value = hours
    const hourText = shortFormat ? 'h' : (hours === 1 ? 'hour' : 'hours')
    relative = `${Math.max(1, hours)} ${hourText}`
  } else if (minutes >= 1 || minUnit === 'minutes') {
    unit = 'minutes'
    value = minutes
    const minuteText = shortFormat ? 'm' : (minutes === 1 ? 'minute' : 'minutes')
    relative = `${Math.max(1, minutes)} ${minuteText}`
  } else if (includeSeconds || minUnit === 'seconds') {
    unit = 'seconds'
    value = seconds
    const secondText = shortFormat ? 's' : (seconds === 1 ? 'second' : 'seconds')
    relative = `${Math.max(1, seconds)} ${secondText}`
  } else {
    // Less than a minute, but not showing seconds
    unit = 'minutes'
    value = 1
    const minuteText = shortFormat ? 'm' : 'minute'
    relative = `1 ${minuteText}`
  }

  // Add directional context
  if (showSuffix) {
    if (shortFormat) {
      relative = isPast ? `${relative} ago` : `in ${relative}`
    } else {
      relative = isPast ? `${relative} ago` : `in ${relative}`
    }
  }

  return {
    relative,
    isPast,
    shouldShowAbsolute: false,
    absolute,
    diffMs,
    unit,
    value: isPast ? -value : value
  }
}

/**
 * Gets a smart relative time that switches to absolute after a threshold
 */
export function getSmartRelativeTime(
  date: Date | string,
  timezone: string = DEFAULT_TIMEZONE,
  options: Omit<RelativeTimeOptions, 'absoluteThreshold'> & { 
    smartThreshold?: number 
  } = {}
): string {
  const { smartThreshold = 7, ...restOptions } = options
  
  const result = getRelativeTime(date, timezone, {
    ...restOptions,
    absoluteThreshold: smartThreshold
  })

  return result.shouldShowAbsolute ? result.absolute : result.relative
}

/**
 * Gets contextual relative time with intelligent formatting
 * Examples: "Just now", "2m", "1h", "Yesterday", "Dec 15", "Dec 15, 2023"
 */
export function getContextualTime(
  date: Date | string,
  timezone: string = DEFAULT_TIMEZONE,
  context: 'compact' | 'normal' | 'detailed' = 'normal'
): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date
  const now = getCurrentTimeInTimezone(timezone)
  const diffMs = Math.abs(dateObj.getTime() - now.getTime())
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  const currentYear = now.getFullYear()
  const dateYear = dateObj.getFullYear()

  if (context === 'compact') {
    if (diffMs < 60 * 1000) return 'now'
    if (diffMs < 60 * 60 * 1000) return `${Math.floor(diffMs / (60 * 1000))}m`
    if (diffMs < 24 * 60 * 60 * 1000) return `${Math.floor(diffMs / (60 * 60 * 1000))}h`
    if (days < 7) return `${days}d`
    if (dateYear === currentYear) return formatDateInTimezone(dateObj, timezone, 'MMM dd')
    return formatDateInTimezone(dateObj, timezone, 'MMM dd, yyyy')
  }

  const result = getRelativeTime(date, timezone, {
    includeSeconds: context === 'detailed',
    minUnit: context === 'detailed' ? 'seconds' : 'minutes',
    absoluteThreshold: context === 'detailed' ? 30 : 7,
    shortFormat: false // context can't be 'compact' here as we handled it above
  })

  if (result.shouldShowAbsolute) {
    // Smart absolute formatting based on context
    if (dateYear === currentYear) {
      return formatDateInTimezone(dateObj, timezone, context === 'detailed' ? 'MMM dd, h:mm a' : 'MMM dd')
    } else {
      return formatDateInTimezone(dateObj, timezone, context === 'detailed' ? 'MMM dd, yyyy h:mm a' : 'MMM dd, yyyy')
    }
  }

  return result.relative
}

/**
 * Gets time until/since with precise calculations
 */
export function getTimeUntil(
  targetDate: Date | string,
  timezone: string = DEFAULT_TIMEZONE,
  options: { 
    includeSeconds?: boolean
    precision?: 'low' | 'medium' | 'high'
  } = {}
): {
  timeString: string
  isPast: boolean
  totalSeconds: number
} {
  const { includeSeconds = false, precision = 'medium' } = options
  const target = typeof targetDate === 'string' ? new Date(targetDate) : targetDate
  const now = getCurrentTimeInTimezone(timezone)
  const diffMs = target.getTime() - now.getTime()
  const isPast = diffMs < 0
  const totalSeconds = Math.floor(Math.abs(diffMs) / 1000)

  if (totalSeconds < 60 && !includeSeconds) {
    return {
      timeString: isPast ? 'Just finished' : 'Starting soon',
      isPast,
      totalSeconds
    }
  }

  const seconds = totalSeconds % 60
  const totalMinutes = Math.floor(totalSeconds / 60)
  const minutes = totalMinutes % 60
  const totalHours = Math.floor(totalMinutes / 60)
  const hours = totalHours % 24
  const days = Math.floor(totalHours / 24)

  const parts: string[] = []

  if (days > 0) parts.push(`${days} day${days !== 1 ? 's' : ''}`)
  
  if (precision === 'high' || (precision === 'medium' && days === 0)) {
    if (hours > 0) parts.push(`${hours} hour${hours !== 1 ? 's' : ''}`)
  }
  
  if (precision === 'high' || (precision === 'medium' && days === 0 && hours < 12)) {
    if (minutes > 0) parts.push(`${minutes} minute${minutes !== 1 ? 's' : ''}`)
  }
  
  if (includeSeconds && precision === 'high' && days === 0 && hours === 0) {
    if (seconds > 0) parts.push(`${seconds} second${seconds !== 1 ? 's' : ''}`)
  }

  if (parts.length === 0) {
    return {
      timeString: includeSeconds ? `${seconds} second${seconds !== 1 ? 's' : ''}` : 'Less than a minute',
      isPast,
      totalSeconds
    }
  }

  const timeString = parts.slice(0, precision === 'high' ? 3 : 2).join(', ')
  
  return {
    timeString,
    isPast,
    totalSeconds
  }
}

// =============================================================================
// GLOBAL TEAM & BUSINESS HOURS FUNCTIONALITY
// =============================================================================

/**
 * Common business timezones for global teams
 */
export const BUSINESS_TIMEZONES = [
  { value: 'UTC', label: 'UTC', abbreviation: 'UTC', region: 'Universal' },
  { value: 'America/New_York', label: 'Eastern Time', abbreviation: 'EST/EDT', region: 'US East Coast' },
  { value: 'America/Chicago', label: 'Central Time', abbreviation: 'CST/CDT', region: 'US Central' },
  { value: 'America/Denver', label: 'Mountain Time', abbreviation: 'MST/MDT', region: 'US Mountain' },
  { value: 'America/Los_Angeles', label: 'Pacific Time', abbreviation: 'PST/PDT', region: 'US West Coast' },
  { value: 'Europe/London', label: 'London', abbreviation: 'GMT/BST', region: 'UK' },
  { value: 'Europe/Paris', label: 'Central European Time', abbreviation: 'CET/CEST', region: 'Central Europe' },
  { value: 'Asia/Tokyo', label: 'Japan Standard Time', abbreviation: 'JST', region: 'Japan' },
  { value: 'Asia/Shanghai', label: 'China Standard Time', abbreviation: 'CST', region: 'China' },
  { value: 'Asia/Kolkata', label: 'India Standard Time', abbreviation: 'IST', region: 'India' },
  { value: 'Australia/Sydney', label: 'Australian Eastern Time', abbreviation: 'AEST/AEDT', region: 'Australia' },
] as const

/**
 * Interface for team member timezone preferences
 */
export interface TeamMemberTimezone {
  id: string
  name?: string
  timezone: string
  workingHours?: {
    start: string // HH:MM format
    end: string   // HH:MM format
    days: number[] // 0-6 (Sunday-Saturday)
  }
}

/**
 * Interface for multi-timezone conversion result
 */
export interface MultiTimezoneConversion {
  timezone: string
  displayName: string
  abbreviation: string
  time: Date
  formatted: string
  isBusinessHours: boolean
  isWeekend: boolean
  offset: string
  warningLevel: 'none' | 'caution' | 'warning'
  warningMessage?: string
}

/**
 * Default business hours (9 AM - 5 PM, Monday-Friday)
 */
export const DEFAULT_BUSINESS_HOURS = {
  start: '09:00',
  end: '17:00',
  days: [1, 2, 3, 4, 5] // Monday-Friday
}

/**
 * Determines if a given time falls within business hours
 */
export function isBusinessHours(
  date: Date | string,
  timezone: string,
  businessHours: { start: string; end: string; days: number[] } = DEFAULT_BUSINESS_HOURS
): boolean {
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date
    const localTime = new Date(dateObj.toLocaleString('en-US', { timeZone: timezone }))
    
    const day = localTime.getDay() // 0 = Sunday, 6 = Saturday
    const hour = localTime.getHours()
    const minute = localTime.getMinutes()
    const timeInMinutes = hour * 60 + minute
    
    // Check if it's a working day
    if (!businessHours.days.includes(day)) {
      return false
    }
    
    // Parse start and end times
    const [startHour, startMinute] = businessHours.start.split(':').map(Number)
    const [endHour, endMinute] = businessHours.end.split(':').map(Number)
    const startTimeInMinutes = startHour * 60 + startMinute
    const endTimeInMinutes = endHour * 60 + endMinute
    
    return timeInMinutes >= startTimeInMinutes && timeInMinutes <= endTimeInMinutes
  } catch (error) {
    console.error('Error checking business hours:', error)
    return false
  }
}

/**
 * Checks if a date falls on a weekend
 */
export function isWeekend(date: Date | string, timezone: string): boolean {
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date
    const localTime = new Date(dateObj.toLocaleString('en-US', { timeZone: timezone }))
    const day = localTime.getDay()
    return day === 0 || day === 6 // Sunday or Saturday
  } catch (error) {
    console.error('Error checking weekend:', error)
    return false
  }
}

/**
 * Converts a time to multiple timezones for global team coordination
 */
export function convertToMultipleTimezones(
  date: Date | string,
  timezones: string[] = BUSINESS_TIMEZONES.map(tz => tz.value),
  teamMembers?: TeamMemberTimezone[]
): MultiTimezoneConversion[] {
  const dateObj = typeof date === 'string' ? new Date(date) : date
  const conversions: MultiTimezoneConversion[] = []

  timezones.forEach(timezone => {
    try {
      // Find team member with this timezone for custom business hours
      const teamMember = teamMembers?.find(member => member.timezone === timezone)
      const businessHours = teamMember?.workingHours || DEFAULT_BUSINESS_HOURS
      
      // Get time in this timezone
      const localTime = new Date(dateObj.toLocaleString('en-US', { timeZone: timezone }))
      const timezoneInfo = getTimezoneInfo(timezone, dateObj)
      const displayName = getTimezoneDisplayName(timezone)
      
      // Check business conditions
      const inBusinessHours = isBusinessHours(dateObj, timezone, businessHours)
      const onWeekend = isWeekend(dateObj, timezone)
      
      // Determine warning level
      let warningLevel: 'none' | 'caution' | 'warning' = 'none'
      let warningMessage: string | undefined
      
      if (onWeekend) {
        warningLevel = 'warning'
        warningMessage = 'Weekend'
      } else if (!inBusinessHours) {
        const hour = localTime.getHours()
        if (hour < 6 || hour > 22) {
          warningLevel = 'warning'
          warningMessage = 'Late night/Early morning'
        } else {
          warningLevel = 'caution'
          warningMessage = 'Outside typical business hours'
        }
      }
      
      conversions.push({
        timezone,
        displayName,
        abbreviation: timezoneInfo.abbreviation,
        time: localTime,
        formatted: formatDateInTimezone(dateObj, timezone, 'MMM dd, h:mm a'),
        isBusinessHours: inBusinessHours,
        isWeekend: onWeekend,
        offset: timezoneInfo.offsetString,
        warningLevel,
        warningMessage
      })
    } catch (error) {
      console.error(`Error converting to timezone ${timezone}:`, error)
    }
  })

  // Sort by offset to group similar timezones together
  return conversions.sort((a, b) => {
    const offsetA = getTimezoneInfo(a.timezone).offset
    const offsetB = getTimezoneInfo(b.timezone).offset
    return offsetA - offsetB
  })
}

/**
 * Suggests better meeting times for global teams
 */
export function suggestGlobalMeetingTimes(
  targetDate: Date | string,
  teamTimezones: string[],
  options: {
    workingHoursOnly?: boolean
    weekdaysOnly?: boolean
    maxSuggestions?: number
    hourRange?: { start: number; end: number }
  } = {}
): Array<{
  time: Date
  score: number
  conversions: MultiTimezoneConversion[]
  summary: string
}> {
  const {
    workingHoursOnly = true,
    weekdaysOnly = true,
    maxSuggestions = 5,
    hourRange = { start: 6, end: 22 } // 6 AM to 10 PM range
  } = options

  const targetDateObj = typeof targetDate === 'string' ? new Date(targetDate) : targetDate
  const suggestions: Array<{
    time: Date
    score: number
    conversions: MultiTimezoneConversion[]
    summary: string
  }> = []

  // Generate potential meeting times around the target date
  const baseDate = new Date(targetDateObj)
  baseDate.setMinutes(0, 0, 0) // Round to nearest hour

  // Check times from 2 days before to 2 days after
  for (let dayOffset = -2; dayOffset <= 2; dayOffset++) {
    for (let hour = hourRange.start; hour <= hourRange.end; hour += 2) { // Check every 2 hours
      const candidateTime = new Date(baseDate)
      candidateTime.setDate(candidateTime.getDate() + dayOffset)
      candidateTime.setHours(hour)

      const conversions = convertToMultipleTimezones(candidateTime, teamTimezones)
      
      // Calculate score based on how many team members would be in business hours
      let score = 0
      let businessHoursCount = 0
      let weekendPenalty = 0
      
      conversions.forEach(conversion => {
        if (conversion.isBusinessHours) {
          score += 10
          businessHoursCount++
        } else if (!conversion.isWeekend) {
          score += 5 // Some points for weekday non-business hours
        }
        
        if (conversion.isWeekend) {
          weekendPenalty += 3
        }
        
        // Penalty for very early or very late times
        const hour = conversion.time.getHours()
        if (hour < 6 || hour > 22) {
          score -= 5
        }
      })

      score -= weekendPenalty

      // Skip if no one would be in business hours and we're filtering for working hours only
      if (workingHoursOnly && businessHoursCount === 0) {
        continue
      }

      // Skip weekends if requested
      if (weekdaysOnly && conversions.some(c => c.isWeekend)) {
        continue
      }

      // Generate summary
      const businessHoursRatio = businessHoursCount / conversions.length
      let summary = `${businessHoursCount}/${conversions.length} in business hours`
      
      if (businessHoursRatio === 1) {
        summary = 'Perfect for all timezones'
      } else if (businessHoursRatio >= 0.7) {
        summary = 'Good for most timezones'
      } else if (businessHoursRatio >= 0.5) {
        summary = 'Acceptable for some timezones'
      } else {
        summary = 'Challenging for most timezones'
      }

      suggestions.push({
        time: candidateTime,
        score,
        conversions,
        summary
      })
    }
  }

  // Sort by score (highest first) and return top suggestions
  return suggestions
    .sort((a, b) => b.score - a.score)
    .slice(0, maxSuggestions)
}

/**
 * Gets timezone-aware scheduling recommendations
 */
export function getSchedulingRecommendations(
  targetTime: Date | string,
  teamTimezones: string[]
): {
  current: MultiTimezoneConversion[]
  recommendations: Array<{
    time: Date
    reason: string
    conversions: MultiTimezoneConversion[]
  }>
  warnings: string[]
} {
  const targetDate = typeof targetTime === 'string' ? new Date(targetTime) : targetTime
  const currentConversions = convertToMultipleTimezones(targetDate, teamTimezones)
  
  const warnings: string[] = []
  const recommendations: Array<{
    time: Date
    reason: string
    conversions: MultiTimezoneConversion[]
  }> = []

  // Analyze current time
  const weekendCount = currentConversions.filter(c => c.isWeekend).length
  const businessHoursCount = currentConversions.filter(c => c.isBusinessHours).length
  const lateNightCount = currentConversions.filter(c => 
    c.warningLevel === 'warning' && c.warningMessage?.includes('night')
  ).length

  // Generate warnings
  if (weekendCount > 0) {
    warnings.push(`${weekendCount} timezone(s) will be on weekend`)
  }
  
  if (businessHoursCount < teamTimezones.length * 0.5) {
    warnings.push('Fewer than half of team members will be in business hours')
  }
  
  if (lateNightCount > 0) {
    warnings.push(`${lateNightCount} timezone(s) will be late night/early morning`)
  }

  // Generate recommendations if there are issues
  if (warnings.length > 0) {
    const suggestions = suggestGlobalMeetingTimes(targetDate, teamTimezones, {
      maxSuggestions: 3
    })
    
    suggestions.forEach(suggestion => {
      recommendations.push({
        time: suggestion.time,
        reason: suggestion.summary,
        conversions: suggestion.conversions
      })
    })
  }

  return {
    current: currentConversions,
    recommendations,
    warnings
  }
}

/**
 * Utility to safely handle timezone operations with fallbacks
 */
export function safeTimezoneOperation<T>(
  operation: () => T,
  fallback: T,
  errorMessage: string = 'Timezone operation failed'
): T {
  try {
    return operation()
  } catch (error) {
    console.error(errorMessage, error)
    return fallback
  }
}

// Default timezone utilities
export const DEFAULT_TIMEZONE = 'UTC'
export const DEFAULT_FORMAT = 'MMM dd, yyyy h:mm a'

// Export commonly used timezones
export const COMMON_TIMEZONES = TIMEZONE_REGIONS.COMMON.timezones

// Export for backwards compatibility and ease of use
export const timezoneUtils = {
  detectUserTimezone,
  isValidTimezone,
  getTimezoneInfo,
  formatDateInTimezone,
  getCurrentTimeInTimezone,
  convertUTCToTimezone,
  convertTimezone,
  getFormattedDateWithTimezone,
  getTimezoneDisplayName,
  getTimezoneAbbreviation,
  isDSTActive,
  getTimezoneOptions,
  formatTimeInTimezone,
  getTimezoneOffsetHours,
  safeTimezoneOperation,
  // Relative time functions
  getRelativeTime,
  getSmartRelativeTime,
  getContextualTime,
  getTimeUntil,
  // Global team functions
  isBusinessHours,
  isWeekend,
  convertToMultipleTimezones,
  suggestGlobalMeetingTimes,
  getSchedulingRecommendations,
  // Constants
  TIMEZONE_REGIONS,
  ALL_TIMEZONES,
  COMMON_TIMEZONES,
  BUSINESS_TIMEZONES,
  DEFAULT_BUSINESS_HOURS,
  DEFAULT_TIMEZONE,
  DEFAULT_FORMAT
}

export default timezoneUtils